import { Trans, useLingui } from "@lingui/react/macro";
import {
  activityKeys,
  updateEndDate,
  type IRecurringActivitySchedule,
} from "@/api/Activities";
import { FilledButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import { Loading } from "@medoma/components/Loading/Loading";
import { Text } from "@medoma/components/Text/Text";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import {
  endOfPlainDateInTimeZone,
  humanFormat,
  toPlainDateInTimeZone,
} from "@medoma/models/date-and-time";
import { useUserProfile } from "@/api/Users";

type IEditEndDateFields = {
  endDate: string;
};

export const EditActivityEndDate = ({
  onSuccess,
  schedule,
  activityId,
}: {
  onSuccess: () => void;
  schedule: IRecurringActivitySchedule;
  activityId: string;
}) => {
  const { t } = useLingui();
  const { timeZone } = useUserProfile();
  const methods = useForm<IEditEndDateFields>({
    defaultValues: {
      endDate: schedule.end
        ? toPlainDateInTimeZone({ instant: schedule.end, timeZone })
        : undefined,
    },
  });

  const {
    formState: { errors, isDirty },
    handleSubmit,
    register,
    setError,
  } = methods;

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (endDateTime: Date) => updateEndDate(activityId, endDateTime),
    onError: (error) => {
      setError("endDate", {
        message: deducedError(error),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: activityKeys.all });
      onSuccess();
    },
  });

  const validateAndSubmit = handleSubmit((formData) => {
    const { endDate } = formData;
    mutate(endOfPlainDateInTimeZone({ date: endDate, timeZone }));
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        {isPending ? (
          <Loading message={t`Ändrar slutdatum`} />
        ) : (
          <>
            {errors.root?.server?.message && !isDirty ? (
              <ErrorMessage message={errors.root.server.message} />
            ) : null}
            <Form.Row>
              <div>
                <Text element="div" weight="medium">{t`Startdatum`}</Text>
                <Text element="div">
                  {humanFormat(schedule.start, {
                    month: "long",
                    day: "numeric",
                  })}
                </Text>
              </div>
            </Form.Row>
            <Form.Row>
              <InputField
                label={t`Slutdatum`}
                description={t`Aktivitetsserien fortsätter fram till och med det slutdatum du anger här`}
                showOptionalLabel={false}
                errorMessage={errors.endDate?.message}
                type="date"
                {...register(`endDate`, {
                  required: {
                    value: true,
                    message: t`Ange slutdatum`,
                  },
                  min: {
                    value: toPlainDateInTimeZone({
                      instant: new Date(),
                      timeZone,
                    }),
                    message: t`Slutdatumet behöver vara idag eller senare`,
                  },
                })}
              />
            </Form.Row>
            <FilledButton type="submit">
              <Trans>Spara ändringar</Trans>
            </FilledButton>
          </>
        )}
      </Form>
    </FormProvider>
  );
};
